/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import React from 'react';
import { CheckboxRadioProps } from '../fields/CheckboxRadio/CheckboxRadio.props';
import { CHECKBOX_RADIO_TYPE_BASIC } from '../fields/CheckboxRadio/CheckboxRadio.types';
import { FormControlOption } from './FormControl.defs';
import { FormControlType } from './types/FormControlType.types';

/**
 * The props for `<FormControl />`.
 */
export interface FormControlProps extends CheckboxRadioProps {
  /**
   * The click event handler for a Checkbox component.
   */
  checkboxClick?(option: FormControlOption): void;

  /**
   * The click event handler for a Radio component.
   */
  customClick?(optionIndex: number): void;

  /**
   * The keyDown event handler for a Radio component.
   */
  customKeyDown?(event: React.KeyboardEvent): void;

  /**
   * The data-id of a form control.
   */
  dataId?: string;

  /**
   * Option data for a single Checkbox or Radio component.
   */
  option: FormControlOption;

  /**
   * Index of this option in checkbox/radio group.
   */
  optionIndex?: number;

  /**
   * Ref for radio button to manage focus.
   */
  radioRef?: React.Ref<HTMLButtonElement>;

  /**
   * The icon to appear next to the Form Control label.
   */
  svg?: JSX.Element;
  /**
   * Tab index for form control.
   */
  tabIndex: 0 | -1;

  /**
   * Field type.
   */
  type: FormControlType;

  /**
   * Field choice value.
   */
  value?: string;
}

/**
 * The default prop values for `<FormControl />`.
 */
export const formControlPropsDefaults = {
  checked: false,
  optionIndex: 0,
  tabIndex: 0,
  variant: CHECKBOX_RADIO_TYPE_BASIC,
};
